import { BranchesOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Space, Table, Tooltip } from "antd";
import Avatar from "antd/lib/avatar/avatar";
import { useState } from "react";
import { useOutletContext } from "react-router";
import { Link } from "react-router-dom";
import DeploymentCard from "../../components/DeploymentCard";
import CardMarketing from "../../components/marketing/CardMarketing";
import TemplatePipelineStatusDrawer from "../../components/template-modules/TemplatePipelineStatusDrawer";
import { Dictionary, IAppDetails } from "../../types";
import DeploymentsImage from "../../assets/deployment-history.png";
import { useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import { fetchProjectLimitations } from "../../utils/local-storage";

interface VisiblePipeline {
    name: string;
    version: string;
    buildId?: string;
    templateExecutionId?: string;
}

export default function AppDeployments() {
    const currentProject = useRecoilValue(currentProjectState);
    const { appDetails } = useOutletContext<{ appDetails: IAppDetails }>();
    const [activeDeployment] = useState(appDetails.deployments.find(d => d.version === appDetails.version));
    const [visiblePipeline, setVisiblePipeline] = useState<VisiblePipeline | null>();
    const limitations = fetchProjectLimitations();

    const isTrialEligible = (planId: string) => {
        return currentProject?.freeTrials && !(currentProject.freeTrials as Dictionary<boolean>)[planId];
    }

    return (
        <Space direction="vertical" size="large" className="full-width">
            {
                !limitations.deploymentInsights &&
                <CardMarketing
                    title="Deployment History"
                    subtitle="Deployment history is available in Advanced Plan ($99/month)."
                    description="Know the state of your apps and infrastructure at any point in time. Follow deployments per environment in real-time, understand who did the changes and drill down to the exact commit."
                    action={
                        <Button>
                            <Link to="/pricing/upgrade">
                                {isTrialEligible("ADVANCED") ? "Start 7 day trial" : "Upgrade to Advanced Plan"}
                            </Link>
                        </Button>
                    }
                    image={DeploymentsImage}
                    imageShadow={true}
                />
            }
            {
                activeDeployment &&
                <DeploymentCard
                    title="Active Deployment"
                    value={activeDeployment}
                    emptyText="No active deployment"
                    extra={
                        <Button
                            onClick={() => setVisiblePipeline({
                                name: appDetails.name,
                                version: activeDeployment?.version,
                                buildId: activeDeployment?.pipeline?.buildId,
                                templateExecutionId: activeDeployment?.pipeline?.id
                            })}
                        >
                            View Pipeline
                        </Button>
                    }
                />
            }

            {
                !!appDetails.deployments.length && limitations.deploymentInsights &&
                <Table
                    title={() => <span>Previous Deployments</span>}
                    showHeader={false}
                    pagination={{ pageSize: 5 }}
                    className="ant-table-wrapper-bordered"
                    columns={[
                        {
                            key: 'deployment',
                            ellipsis: true,
                            render: deployment =>
                                <>
                                    {
                                        deployment.status.name !== "DEPLOYED" &&
                                        <Tooltip
                                            placement="topLeft"
                                            title={<pre className="white-space-pre-wrap">{deployment.status.error}</pre>}
                                        >
                                            <ExclamationCircleOutlined style={{ color: "red" }} />&nbsp;
                                        </Tooltip>
                                    }
                                    <Tooltip placement="topLeft" title={
                                        <Space direction="vertical">
                                            {deployment.version}
                                            Click for pipeline details.
                                        </Space>
                                    }>
                                        <Button
                                            type="link"
                                            style={{ paddingLeft: 0 }}
                                            onClick={() => setVisiblePipeline({
                                                name: appDetails.name,
                                                version: deployment.version,
                                                buildId: deployment.pipeline?.buildId,
                                                templateExecutionId: deployment.pipeline?.id
                                            })}
                                        >
                                            {deployment.commitType === "branch" ? deployment.version.substring(0, 7) : deployment.version}
                                        </Button>
                                    </Tooltip>
                                </>
                        },
                        {
                            key: 'commitMessage',
                            width: "40%",
                            render: deployment => <>
                                <div>
                                    {deployment.commitMessage}
                                </div>
                                <div>
                                    {deployment.repositoryUrl}
                                </div>
                                {
                                    deployment.branch && <>{deployment.createdAt} on <BranchesOutlined /> {deployment.branch}</>
                                }
                            </>
                        },
                        {
                            key: 'createdBy',
                            align: "right",
                            render: deployment =>
                                <Tooltip title={deployment.initiator?.name || deployment.initiator?.email}>
                                    {deployment.timestamp} <Avatar src={deployment.initiator?.avatar} size="small" />
                                </Tooltip>
                        },
                    ]}
                    dataSource={appDetails.deployments}
                />
            }
            <TemplatePipelineStatusDrawer
                title={`${visiblePipeline?.name} pipeline`}
                footer={`Application deployment logs for version: ${visiblePipeline?.version}`}
                visible={!!visiblePipeline}
                onClose={() => setVisiblePipeline(null)}
                buildId={visiblePipeline?.buildId}
                templateExecutionId={visiblePipeline?.templateExecutionId}
            />
        </Space>
    )
}